import React, { useState, useEffect, useRef } from 'react';
import { userAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { toast } from 'sonner';
import {
    User, Lock, Bell, Shield, FileText,
    Upload, Trash2, Loader2, CheckCircle, AlertCircle, Camera
} from 'lucide-react';

const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const ProfilePictureSection = ({ user, onRefresh }) => {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!['image/jpeg','image/png','image/webp','image/gif'].includes(file.type)) {
            toast.error('Please upload a JPEG, PNG, WebP, or GIF image'); return;
        }
        if (file.size > 2 * 1024 * 1024) { toast.error('Image must be less than 2MB'); return; }
        setUploading(true);
        try {
            await userAPI.uploadProfilePicture(file);
            toast.success('Profile picture updated');
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload picture');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Remove your profile picture?')) return;
        setDeleting(true);
        try {
            await userAPI.deleteProfilePicture();
            toast.success('Profile picture removed');
            if (onRefresh) onRefresh();
        } catch (error) { toast.error('Failed to remove picture'); }
        finally { setDeleting(false); }
    };

    return (
        <div className="flex items-center gap-6 pb-6 mb-6 border-b" data-testid="profile-picture-section">
            <div className="relative group">
                <Avatar className="h-20 w-20 ring-2 ring-neutral-200">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-primary-100 text-primary-700 text-xl font-semibold">
                        {getInitials(user?.name)}
                    </AvatarFallback>
                </Avatar>
                <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                    {uploading ? <Loader2 className="w-6 h-6 text-white animate-spin" /> : <Camera className="w-6 h-6 text-white" />}
                </button>
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleUpload} className="hidden" />
            </div>
            <div className="flex-1">
                <h3 className="font-medium text-neutral-900">{user?.name || 'Your Name'}</h3>
                <p className="text-sm text-neutral-500">{user?.email}</p>
                <div className="flex gap-2 mt-3">
                    <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-picture-btn">
                        {uploading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Uploading...</> : <><Camera className="w-4 h-4 mr-1" /> {user?.picture ? 'Change Photo' : 'Upload Photo'}</>}
                    </Button>
                    {user?.picture && (
                        <Button variant="outline" size="sm" onClick={handleDelete} disabled={deleting} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="delete-picture-btn">
                            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-1" /> Remove</>}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

const SettingsContent = ({ user, refreshUser }) => {
    const [loading, setLoading] = useState(false);
    const [profileForm, setProfileForm] = useState({ name: '', email: '', phone: '' });
    const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });

    useEffect(() => {
        if (user) {
            setProfileForm({ name: user.name || '', email: user.email || '', phone: user.phone || '' });
        }
    }, [user]);

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await userAPI.updateProfile({ name: profileForm.name, phone: profileForm.phone });
            toast.success('Profile updated successfully');
            if (refreshUser) refreshUser();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update profile');
        } finally { setLoading(false); }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (passwordForm.new_password !== passwordForm.confirm_password) { toast.error('New passwords do not match'); return; }
        if (passwordForm.new_password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
        setLoading(true);
        try {
            await userAPI.changePassword({ current_password: passwordForm.current_password, new_password: passwordForm.new_password });
            toast.success('Password changed successfully');
            setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to change password');
        } finally { setLoading(false); }
    };

    return (
        <div data-testid="settings-tab">
            <Tabs defaultValue="profile" className="space-y-6">
                <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="profile" className="flex items-center gap-2" data-testid="settings-tab-profile">
                        <User className="w-4 h-4" /> Profile
                    </TabsTrigger>
                    <TabsTrigger value="security" className="flex items-center gap-2" data-testid="settings-tab-security">
                        <Lock className="w-4 h-4" /> Security
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="profile">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2"><User className="w-5 h-5" /> Profile Information</CardTitle>
                            <CardDescription>Update your personal information and profile picture</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <ProfilePictureSection user={user} onRefresh={refreshUser} />
                            <form onSubmit={handleProfileUpdate} className="space-y-6" data-testid="profile-form">
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="name">Full Name</Label>
                                        <Input id="name" data-testid="input-name" value={profileForm.name} onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Your full name" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email Address</Label>
                                        <Input id="email" type="email" data-testid="input-email" value={profileForm.email} disabled className="bg-neutral-100" />
                                        <p className="text-xs text-neutral-500">Email cannot be changed</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="phone">Phone Number</Label>
                                        <Input id="phone" data-testid="input-phone" value={profileForm.phone} onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="+44 7XXX XXXXXX" />
                                    </div>
                                </div>
                                <div className="flex justify-end">
                                    <Button type="submit" disabled={loading} data-testid="save-profile-btn">
                                        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save Changes'}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="security">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5" /> Security Settings</CardTitle>
                            <CardDescription>Manage your password</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handlePasswordChange} className="space-y-6" data-testid="password-form">
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="current_password">Current Password</Label>
                                        <Input id="current_password" type="password" data-testid="input-current-password" value={passwordForm.current_password} onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))} placeholder="Enter current password" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="new_password">New Password</Label>
                                        <Input id="new_password" type="password" data-testid="input-new-password" value={passwordForm.new_password} onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))} placeholder="Enter new password" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="confirm_password">Confirm New Password</Label>
                                        <Input id="confirm_password" type="password" data-testid="input-confirm-password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))} placeholder="Confirm new password" />
                                    </div>
                                </div>
                                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                    <div className="flex items-start gap-3">
                                        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                                        <div className="text-sm text-amber-800">
                                            <p className="font-medium">Password Requirements</p>
                                            <ul className="list-disc list-inside mt-1 space-y-1">
                                                <li>At least 8 characters long</li>
                                                <li>Include uppercase and lowercase letters</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-end">
                                    <Button type="submit" disabled={loading} data-testid="change-password-btn">
                                        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Changing...</> : 'Change Password'}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default SettingsContent;
